import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const DISCLAIMER_TEXT = `The information, stock price predictions, forecasts, analytics, and other content provided on this portal are for informational and educational purposes only.

This platform does not provide investment advice, financial advice, trading advice, or any other form of regulated financial service. All predictions are generated using automated algorithms and historical data models and are not guarantees of future performance.

Financial markets involve significant risk, including the potential loss of capital. Past performance and model-based projections do not ensure future results.

Users are solely responsible for their investment decisions. Before making any financial or trading decisions, users should conduct their own research and consult a qualified financial advisor or licensed professional.

The owners, developers, operators, and affiliates of this platform shall not be held liable for any direct, indirect, incidental, or consequential losses or damages arising from the use of this portal or reliance on its content.

By accessing or using this platform, you acknowledge and agree to this disclaimer.`;

export function DisclaimerModal({ children }) {
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);

  // Check if user has accepted disclaimer in this SESSION only
  // Using sessionStorage so disclaimer must be accepted on every new session
  useEffect(() => {
    const accepted = sessionStorage.getItem("stocksense_disclaimer_accepted");
    if (accepted === "true") {
      setHasAgreed(true);
    } else {
      setShowDisclaimer(true);
    }
  }, []);

  const handleAcceptDisclaimer = () => {
    if (!disclaimerAccepted) {
      toast.error("Please check the box to confirm you agree to the disclaimer");
      return;
    }
    // Store in sessionStorage (clears when browser/tab closes)
    sessionStorage.setItem("stocksense_disclaimer_accepted", "true");
    setHasAgreed(true);
    setShowDisclaimer(false);
  };

  // If disclaimer accepted, render children
  if (hasAgreed) {
    return <>{children}</>;
  }

  // Show disclaimer modal
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <Dialog open={showDisclaimer} onOpenChange={() => {}}>
        <DialogContent className="max-w-2xl bg-gray-900 border-gray-700" data-testid="disclaimer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <AlertTriangle className="h-6 w-6 text-yellow-500" />
              Important Disclaimer
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Please read and accept the following disclaimer before accessing StockSense
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[300px] rounded-md border border-gray-700 p-4 bg-gray-800/50">
            <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">
              {DISCLAIMER_TEXT}
            </div>
          </ScrollArea>

          <div className="flex items-start space-x-3 mt-4">
            <Checkbox
              id="disclaimer-agree"
              checked={disclaimerAccepted}
              onCheckedChange={(checked) => setDisclaimerAccepted(checked)}
              data-testid="disclaimer-checkbox"
              className="mt-1"
            />
            <label
              htmlFor="disclaimer-agree"
              className="text-sm text-gray-300 cursor-pointer leading-relaxed"
            >
              I have read, understood, and agree to the above disclaimer. I acknowledge that all predictions are for informational purposes only and I am solely responsible for my investment decisions.
            </label>
          </div>

          <DialogFooter className="mt-4">
            <Button
              onClick={handleAcceptDisclaimer}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="accept-disclaimer-btn"
            >
              I Agree & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DisclaimerModal;
